<template>
  <div class="guide-annotations">
    <h2>What are annotations?</h2>
    <p>
      Annotations are the interactive fields signers complete during the signing
      ceremony. They define where each signer must act on each document page,
      whether that means drawing a signature, typing initials, entering a date,
      checking a box, or uploading an attachment. Without annotations, a document
      is just a PDF with nothing to sign.
    </p>
    <p>
      Each annotation is placed on a specific page at specific coordinates and
      assigned to a specific signer. When that signer opens the signing ceremony,
      the system highlights their assigned fields and guides them through completion
      in order.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Field types</h2>
    <p>
      The platform supports a wide range of field types to cover most document
      signing scenarios. Each type dictates what the signer sees and what action
      they must take.
    </p>

    <div class="q-my-md" style="overflow-x: auto;">
      <table class="q-table" style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Type</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Description</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Signer action</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Signature</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Full legal signature</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Draw or type signature</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Initials</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Abbreviated initials</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Draw or type initials</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>DateSigned</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Auto-filled signing date</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Automatic (no action)</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Date</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Manual date entry</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Enter a date</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Text</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Free-form text</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Type text</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Checkbox</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Boolean toggle</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Check/uncheck</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>RadioGroup</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Mutually exclusive options</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Select one option</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Dropdown</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Selection from list</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Choose from dropdown</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>FullName</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Signer's full name</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Auto-filled</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Email</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Signer's email</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Auto-filled</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Company</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Company name</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Type text</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Title</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Job title</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Type text</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Number</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Numeric value</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Enter number</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Attachment</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">File upload</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Upload a file</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Formula</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Computed value based on other fields</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Auto-filled (no action)</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Note</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Read-only text</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">No action (informational)</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Approve</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Explicit approval action (Approver signer type)</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Click to approve</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>Decline</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Explicit decline action (Approver signer type)</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Click to decline</td>
          </tr>
        </tbody>
      </table>
    </div>

    <q-separator class="q-my-lg" />

    <h2>Coordinate system</h2>
    <q-banner class="bg-amber-1 text-amber-10 q-mb-lg" rounded>
      <template v-slot:avatar>
        <q-icon name="warning" color="amber-10" />
      </template>
      <strong>Coordinates are percentages, not pixels.</strong> The <code>x</code>,
      <code>y</code>, <code>width</code>, and <code>height</code> fields on an
      annotation are expressed as 0 to 100 percentages of the page's dimensions. An
      annotation with <code>x: 50, y: 80</code> sits at horizontal center, 80% down
      the page. This makes annotation layouts resolution-independent across page
      sizes.
    </q-banner>
    <ul>
      <li><code>x</code> and <code>y</code>. Top-left corner of the field, as a percentage of page width and height.</li>
      <li><code>width</code> and <code>height</code>. Size of the field, as a percentage of page width and height. Default to 100 and 30 if omitted.</li>
      <li><code>pageNumber</code>. 1-based page index. Page 1 is the first page.</li>
    </ul>

    <q-banner class="bg-red-1 text-red-9 q-mb-lg" rounded>
      <template v-slot:avatar>
        <q-icon name="priority_high" color="red-9" />
      </template>
      <strong>Signer-index gotcha:</strong> Annotations use 0-based
      <code>signerIndex</code>, where <code>0</code> is the first signer. Text tags
      use 1-based indices, where <code>1</code> is the first signer, in every
      format (Platform, DocuSign, Adobe). When you convert from one system to the
      other, remember to subtract or add one. This mismatch is the single most
      common integration error.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h2>Placing fields in the portal</h2>
    <p>
      In the package builder, click a document to open the PDF annotator. A floating
      toolbar on the left lets you drag field types onto the page: Signature,
      Initials, Date, Text, Checkbox, and so on. Each field is assigned to one of
      the signers you have added. A colored border indicates which signer owns that
      field. Resize, drag, or delete any field by clicking it. Changes save
      automatically as you work.
    </p>
    <p>
      For documents with embedded text tags, for example
      <code v-pre>{{Signature:1:true:Sign here:}}</code>, the portal's upload flow
      detects the tags and offers to auto-place the corresponding fields with one
      click. See <a href="/Docs/IntegrationGuides?topic=text-tags">Text Tags</a>
      for how to prepare tagged documents.
    </p>

    <section class="dev-section">
    <h2>For developers and enterprise integration</h2>
    <p>
      Field placement via API supports three modes: manual (x and y percentages),
      anchor-text (find-and-place relative to matching text), and text-tag
      auto-detection. Use bulk creation for packages with many fields. Call the
      validation endpoint before sending to catch misconfigurations.
    </p>

    <h3>Three ways to place fields</h3>
    <p>
      Position annotations on a page by one of three methods. Pick based on how much
      you know about the document layout at placement time.
    </p>

    <h3>Manual coordinates (percentage-based)</h3>
    <p>
      Specify exact page, x, y, width, and height. Best for known, fixed layouts,
      such as a standard contract where the signature line always lands at the same
      relative position.
    </p>

    <GuideCodeTabs :samples="samples.createAnnotation" />

    <h3>Anchor-text placement</h3>
    <p>
      Rather than computing coordinates, let the system position a field relative to
      matching text in the document. Provide <code>anchorText</code> and optional
      pixel offsets. The system finds the first occurrence of the text and places
      the field at that location plus the offsets.
    </p>

    <GuideCodeTabs :samples="samples.anchorTextBody" />

    <p>
      Anchor offsets are in pixels (unlike the percentage-based <code>x</code> and
      <code>y</code>). This helps for documents where the exact position of a
      signature line shifts based on the length of content above it.
    </p>

    <h3>Text tag auto-detection</h3>
    <p>
      Embed marker strings like <code v-pre>{{Signature:1:true:Sign Here:}}</code>
      directly in the document text (typically in DOCX templates). Upload the
      document, detect the tags, and apply them in one call:
    </p>

    <GuideCodeTabs :samples="samples.detectAndApply" />
    <p>
      The second call is idempotent. It skips documents that already have
      annotations, so it is safe to call multiple times. Tag signer indices are
      1-based. The created annotations correctly use 0-based indices internally.
      See the <a href="/Docs/IntegrationGuides?topic=text-tags">Text Tags</a> guide
      for the full tag syntax across all three supported formats.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Signer assignment</h3>
    <p>
      Each annotation is assigned to a signer by <code>signerIndex</code>, a
      0-based integer corresponding to the signer's position in the package's
      signer list. Signer 0 is the first signer added to the package, signer 1 is
      the second, and so on. You can place annotations before or after adding
      signers, as long as the indices align when the package is sent. The
      validation endpoint catches any mismatches.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Required vs. optional</h3>
    <p>
      By default, all annotations are required. A required field must be completed
      before the signer can finish their portion of the signing ceremony. Optional
      fields (<code>"required": false</code>) can be skipped, which is useful for
      supplemental information like a phone number or comments field that the
      signer may or may not wish to provide.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Field groups</h3>
    <p>
      Use the <code>group</code> property to create radio-button groups or
      logically related fields. Fields that share the same group name are treated
      as mutually exclusive selections. The signer picks exactly one option from
      the group.
    </p>
    <GuideCodeTabs :samples="samples.radioGroupExample" />

    <q-separator class="q-my-lg" />

    <h3>Validation</h3>
    <p>
      Before sending a package, call the annotation validation endpoint to verify
      that your field configuration is correct. This catches common setup mistakes
      early and prevents send failures.
    </p>
    <GuideCodeTabs :samples="samples.validateAnnotations" />
    <p>Validation checks include:</p>
    <ul>
      <li>Every signer has at least one required field assigned.</li>
      <li>All annotations reference valid documents within the package.</li>
      <li>All signer indices correspond to actual signers in the package.</li>
      <li>No overlapping required fields on the same page position.</li>
    </ul>

    <q-separator class="q-my-lg" />

    <h3>Bulk creation</h3>
    <p>
      For packages with many fields, use the bulk endpoint to create up to 500
      annotations in a single request. This is much more efficient than creating
      annotations one at a time, especially for documents with dozens of fields.
    </p>
    <GuideCodeTabs :samples="samples.bulkCreate" />
    <p>
      Atomicity is per document group. If any annotation for a given document fails
      validation, the entire set of annotations for that document is rolled back.
      Annotations targeting other documents in the same request may still be
      created. The request size cap is 500 annotations total.
    </p>

    <q-separator class="q-my-lg" />

    <h3>Best practices</h3>
    <ul>
      <li>
        <strong>Use text-tag detection for template-driven workflows.</strong>
        Faster and less error-prone than specifying manual coordinates, especially
        when document content varies in length.
      </li>
      <li>
        <strong>Always validate before sending.</strong> The validation endpoint
        catches common configuration mistakes, including missing signer
        assignments, invalid document references, and overlapping fields, before
        the package enters the sent state.
      </li>
      <li>
        <strong>Place signature fields near the bottom of the last page.</strong>
        This matches the natural signing flow users expect and reduces confusion
        during the ceremony.
      </li>
      <li>
        <strong>Prefer bulk creation over individual calls.</strong> For packages
        with more than a handful of fields, the bulk endpoint reduces round trips.
      </li>
    </ul>

    <q-banner class="bg-blue-1 text-blue-9 q-mt-lg" rounded>
      <strong>Tip:</strong> If your documents are generated from a template system,
      such as a CRM or document-assembly tool, embed text tags during generation
      and use the auto-detect flow. This eliminates the need to calculate pixel
      coordinates entirely.
    </q-banner>

    <q-banner class="bg-green-1 text-green-9 q-mt-md" rounded>
      <template v-slot:avatar>
        <q-icon name="menu_book" color="green-9" />
      </template>
      See the <strong>Annotations</strong> section in the
      <a href="/Docs/ApiReference" class="text-green-9" style="text-decoration: underline;">API Reference</a>
      for complete endpoint schemas and try-it-out examples.
    </q-banner>
    </section>
  </div>
</template>

<script>
import GuideCodeTabs from './shared/GuideCodeTabs.vue';

const samples = {
    createAnnotation: {
        curl: `curl -X POST https://api.esignlaunchpad.com/api/v1/packages/$PACKAGE_ID/annotations \\
  -H "Authorization: Bearer $ESIGN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "documentId": "doc-guid",
    "signerIndex": 0,
    "type": "Signature",
    "pageNumber": 1,
    "x": 12, "y": 82,
    "width": 25, "height": 6,
    "required": true,
    "label": "Customer Signature"
  }'`,

        js: `await fetch(
  \`https://api.esignlaunchpad.com/api/v1/packages/\${packageId}/annotations\`,
  {
    method: 'POST',
    headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      documentId,
      signerIndex: 0,
      type: 'Signature',
      pageNumber: 1,
      x: 12, y: 82,
      width: 25, height: 6,
      required: true,
      label: 'Customer Signature'
    })
  }
);`,

        node: `const response = await fetch(
  \`https://api.esignlaunchpad.com/api/v1/packages/\${packageId}/annotations\`,
  {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      documentId,
      signerIndex: 0,
      type: 'Signature',
      pageNumber: 1,
      x: 12, y: 82,
      width: 25, height: 6,
      required: true,
      label: 'Customer Signature'
    })
  }
);
if (!response.ok) throw new Error(\`\${response.status} \${await response.text()}\`);
const annotation = await response.json();`,

        python: `response = requests.post(
    f'https://api.esignlaunchpad.com/api/v1/packages/{package_id}/annotations',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={
        'documentId': document_id,
        'signerIndex': 0,
        'type': 'Signature',
        'pageNumber': 1,
        'x': 12, 'y': 82,
        'width': 25, 'height': 6,
        'required': True,
        'label': 'Customer Signature'
    }
)
response.raise_for_status()
annotation = response.json()`,

        csharp: `var payload = new
{
    documentId,
    signerIndex = 0,
    type = "Signature",
    pageNumber = 1,
    x = 12, y = 82,
    width = 25, height = 6,
    required = true,
    label = "Customer Signature"
};

var response = await client.PostAsJsonAsync($"api/v1/packages/{packageId}/annotations", payload);
response.EnsureSuccessStatusCode();
var annotation = await response.Content.ReadFromJsonAsync<JsonElement>();`,

        php: `$response = $client->post("api/v1/packages/{$packageId}/annotations", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => [
        'documentId' => $documentId,
        'signerIndex' => 0,
        'type' => 'Signature',
        'pageNumber' => 1,
        'x' => 12, 'y' => 82,
        'width' => 25, 'height' => 6,
        'required' => true,
        'label' => 'Customer Signature',
    ],
]);
$annotation = json_decode($response->getBody(), true);`,

        ruby: `uri = URI("https://api.esignlaunchpad.com/api/v1/packages/#{package_id}/annotations")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = {
  documentId: document_id,
  signerIndex: 0,
  type: 'Signature',
  pageNumber: 1,
  x: 12, y: 82,
  width: 25, height: 6,
  required: true,
  label: 'Customer Signature'
}.to_json

res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
annotation = JSON.parse(res.body)`
    },

    anchorTextBody: {
        json: `{
  "documentId": "doc-guid",
  "signerIndex": 0,
  "type": "Signature",
  "pageNumber": 1,
  "anchorText": "Customer Signature:",
  "anchorOffsetX": 120,
  "anchorOffsetY": -5,
  "width": 25, "height": 6
}`
    },

    detectAndApply: {
        curl: `# 1. Detect embedded text tags in a document
curl -X POST https://api.esignlaunchpad.com/api/v1/packages/$PACKAGE_ID/documents/$DOC_ID/detect-tags \\
  -H "Authorization: Bearer $ESIGN_TOKEN"

# 2. Apply detected tags as annotations (idempotent)
curl -X POST https://api.esignlaunchpad.com/api/v1/packages/$PACKAGE_ID/apply-detected-tags \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `const headers = { 'Authorization': 'Bearer ' + ESIGN_TOKEN };
const base = 'https://api.esignlaunchpad.com/api/v1';

await fetch(\`\${base}/packages/\${packageId}/documents/\${docId}/detect-tags\`, { method: 'POST', headers });
await fetch(\`\${base}/packages/\${packageId}/apply-detected-tags\`,               { method: 'POST', headers });`,

        node: `const headers = { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN };
const base = 'https://api.esignlaunchpad.com/api/v1';

const detect = await fetch(\`\${base}/packages/\${packageId}/documents/\${docId}/detect-tags\`, { method: 'POST', headers });
if (!detect.ok) throw new Error(await detect.text());

const apply = await fetch(\`\${base}/packages/\${packageId}/apply-detected-tags\`, { method: 'POST', headers });
if (!apply.ok) throw new Error(await apply.text());`,

        python: `base = 'https://api.esignlaunchpad.com/api/v1'
headers = {'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}

requests.post(f'{base}/packages/{package_id}/documents/{doc_id}/detect-tags', headers=headers).raise_for_status()
requests.post(f'{base}/packages/{package_id}/apply-detected-tags',           headers=headers).raise_for_status()`,

        csharp: `(await client.PostAsync($"api/v1/packages/{packageId}/documents/{docId}/detect-tags", null))
    .EnsureSuccessStatusCode();
(await client.PostAsync($"api/v1/packages/{packageId}/apply-detected-tags", null))
    .EnsureSuccessStatusCode();`,

        php: `$client->post("api/v1/packages/{$packageId}/documents/{$docId}/detect-tags",
    ['headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')]]);
$client->post("api/v1/packages/{$packageId}/apply-detected-tags",
    ['headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')]]);`,

        ruby: `def post(path)
  uri = URI("https://api.esignlaunchpad.com#{path}")
  req = Net::HTTP::Post.new(uri)
  req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
  Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
end

post("/api/v1/packages/#{package_id}/documents/#{doc_id}/detect-tags")
post("/api/v1/packages/#{package_id}/apply-detected-tags")`
    },

    radioGroupExample: {
        js: `// Two radio options in the same group behave as mutually exclusive choices.
{ "type": "RadioGroup", "group": "payment-method", "label": "Credit Card",    ... }
{ "type": "RadioGroup", "group": "payment-method", "label": "Wire Transfer",  ... }`
    },

    validateAnnotations: {
        curl: `curl -X POST https://api.esignlaunchpad.com/api/v1/packages/$PACKAGE_ID/annotations/validate \\
  -H "Authorization: Bearer $ESIGN_TOKEN"`,

        js: `const response = await fetch(
  \`https://api.esignlaunchpad.com/api/v1/packages/\${packageId}/annotations/validate\`,
  { method: 'POST', headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN } }
);
const { isValid, errors } = await response.json();`,

        node: `const response = await fetch(
  \`https://api.esignlaunchpad.com/api/v1/packages/\${packageId}/annotations/validate\`,
  { method: 'POST', headers: { 'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN } }
);
const { isValid, errors } = await response.json();
if (!isValid) console.error('Validation failed:', errors);`,

        python: `response = requests.post(
    f'https://api.esignlaunchpad.com/api/v1/packages/{package_id}/annotations/validate',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"}
)
response.raise_for_status()
result = response.json()
if not result['isValid']:
    print('Validation failed:', result['errors'])`,

        csharp: `var response = await client.PostAsync(
    $"api/v1/packages/{packageId}/annotations/validate", content: null);
response.EnsureSuccessStatusCode();
var result = await response.Content.ReadFromJsonAsync<JsonElement>();`,

        php: `$response = $client->post("api/v1/packages/{$packageId}/annotations/validate", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
]);
$result = json_decode($response->getBody(), true);`,

        ruby: `uri = URI("https://api.esignlaunchpad.com/api/v1/packages/#{package_id}/annotations/validate")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }
result = JSON.parse(res.body)`
    },

    bulkCreate: {
        curl: `curl -X POST https://api.esignlaunchpad.com/api/v1/packages/$PACKAGE_ID/annotations/bulk \\
  -H "Authorization: Bearer $ESIGN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "annotations": [
      { "documentId": "doc-guid", "signerIndex": 0, "type": "Signature",  "pageNumber": 3, "x": 12, "y": 85, "width": 25, "height": 6, "required": true },
      { "documentId": "doc-guid", "signerIndex": 0, "type": "DateSigned", "pageNumber": 3, "x": 45, "y": 85, "width": 15, "height": 4, "required": true }
    ]
  }'`,

        js: `await fetch(
  \`https://api.esignlaunchpad.com/api/v1/packages/\${packageId}/annotations/bulk\`,
  {
    method: 'POST',
    headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      annotations: [
        { documentId, signerIndex: 0, type: 'Signature',  pageNumber: 3, x: 12, y: 85, width: 25, height: 6, required: true },
        { documentId, signerIndex: 0, type: 'DateSigned', pageNumber: 3, x: 45, y: 85, width: 15, height: 4, required: true }
      ]
    })
  }
);`,

        node: `const annotations = [
  { documentId, signerIndex: 0, type: 'Signature',  pageNumber: 3, x: 12, y: 85, width: 25, height: 6, required: true },
  { documentId, signerIndex: 0, type: 'DateSigned', pageNumber: 3, x: 45, y: 85, width: 15, height: 4, required: true }
];

const response = await fetch(
  \`https://api.esignlaunchpad.com/api/v1/packages/\${packageId}/annotations/bulk\`,
  {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ annotations })
  }
);
if (!response.ok) throw new Error(\`\${response.status} \${await response.text()}\`);`,

        python: `annotations = [
    {'documentId': doc_id, 'signerIndex': 0, 'type': 'Signature',  'pageNumber': 3, 'x': 12, 'y': 85, 'width': 25, 'height': 6, 'required': True},
    {'documentId': doc_id, 'signerIndex': 0, 'type': 'DateSigned', 'pageNumber': 3, 'x': 45, 'y': 85, 'width': 15, 'height': 4, 'required': True},
]

response = requests.post(
    f'https://api.esignlaunchpad.com/api/v1/packages/{package_id}/annotations/bulk',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={'annotations': annotations}
)
response.raise_for_status()`,

        csharp: `var annotations = new[] {
    new { documentId, signerIndex = 0, type = "Signature",  pageNumber = 3, x = 12, y = 85, width = 25, height = 6, required = true },
    new { documentId, signerIndex = 0, type = "DateSigned", pageNumber = 3, x = 45, y = 85, width = 15, height = 4, required = true },
};

var response = await client.PostAsJsonAsync(
    $"api/v1/packages/{packageId}/annotations/bulk",
    new { annotations });
response.EnsureSuccessStatusCode();`,

        php: `$annotations = [
    ['documentId' => $documentId, 'signerIndex' => 0, 'type' => 'Signature',  'pageNumber' => 3, 'x' => 12, 'y' => 85, 'width' => 25, 'height' => 6, 'required' => true],
    ['documentId' => $documentId, 'signerIndex' => 0, 'type' => 'DateSigned', 'pageNumber' => 3, 'x' => 45, 'y' => 85, 'width' => 15, 'height' => 4, 'required' => true],
];

$client->post("api/v1/packages/{$packageId}/annotations/bulk", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => ['annotations' => $annotations],
]);`,

        ruby: `annotations = [
  { documentId: document_id, signerIndex: 0, type: 'Signature',  pageNumber: 3, x: 12, y: 85, width: 25, height: 6, required: true },
  { documentId: document_id, signerIndex: 0, type: 'DateSigned', pageNumber: 3, x: 45, y: 85, width: 15, height: 4, required: true },
]

uri = URI("https://api.esignlaunchpad.com/api/v1/packages/#{package_id}/annotations/bulk")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = { annotations: annotations }.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    }
};

export default {
    name: 'GuideAnnotations',
    components: { GuideCodeTabs },
    data() { return { samples }; }
};
</script>

